import React, { useState } from "react";

import { useGetIdentity, useNotification, useTranslation } from "@refinedev/core";

import { Box, Card, CardContent, FormControl, InputLabel, OutlinedInput, Stack, Typography } from "@mui/material";

import { CardButton } from "@/components/common";
import { IUser } from "@/types";
import { EMAIL_REGEX } from "@/utils";
import { CHANGE_USER_EMAIL_MUTATION, ChangeEmailInput, ChangeUserEmailData } from "@/utils/GraphQLClient";

import { useMutation } from "urql";

import { Breadcrumb } from "./Breadcrumb";
import { EmailVerificationModal } from "./EmailVerificationModal";

export const ChangeEmailPanel: React.FC = () => {
  const { translate } = useTranslation();
  const { data: user, isLoading: isLoadingIdentity, refetch: refetchIdentity } = useGetIdentity<IUser>();
  const { open: openNotification } = useNotification();

  // urql mutation hook
  const [changeEmailResult, changeEmailMutation] = useMutation<ChangeUserEmailData, { input: ChangeEmailInput }>(
    CHANGE_USER_EMAIL_MUTATION
  );
  const isSubmitting = changeEmailResult.fetching;

  // Form state
  const [newEmail, setNewEmail] = useState("");

  // UI state
  const [error, setError] = useState<string | null>(null);

  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(e.target.value);
    setError(null);
  };

  const isValidEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
  };

  const isFormValid = newEmail.trim() !== "" && isValidEmail(newEmail.trim());

  const handleSubmit = async () => {
    setError(null);

    try {
      const result = await changeEmailMutation({
        input: { newEmail: newEmail.trim() },
      });

      if (result.error) {
        setError(
          result.error.graphQLErrors?.[0]?.message ??
            result.error.message ??
            translate("pages.profile.changeEmail.error")
        );
        return;
      }

      // Extract masked email from response
      const deliveryDetails = result.data?.changeUserEmail?.CodeDeliveryDetailsList?.[0];
      const destination = deliveryDetails?.Destination ?? newEmail;

      setMaskedEmail(destination);
      setShowVerificationModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : translate("pages.profile.changeEmail.error"));
    }
  };

  const handleVerificationSuccess = async () => {
    setShowVerificationModal(false);
    setNewEmail("");

    // Refresh identity to update user data across the app
    await refetchIdentity();

    openNotification?.({
      type: "success",
      message: translate("pages.profile.changeEmail.success"),
    });
  };

  const handleVerificationClose = () => {
    setShowVerificationModal(false);
  };

  const isLoading = isLoadingIdentity || isSubmitting;

  return (
    <Box>
      <Breadcrumb
        items={[
          { label: translate("pages.profile.sections.profileAndSecurity"), to: "/profile?section=profile-security" },
          { label: translate("pages.profile.options.changeEmail") },
        ]}
      />

      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            {/* Current Email (read-only) */}
            <Box>
              <Typography variant="h5">
                <Typography component="span" variant="inherit" color="primary">
                  {translate("pages.profile.changeEmail.currentEmail")}
                </Typography>
                <Typography component="span" variant="inherit" data-testid="current-email">
                  {user?.email ?? ""}
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.hint" mt={0.5}>
                {translate("pages.profile.changeEmail.currentEmailHint")}
              </Typography>
            </Box>

            {/* New Email field */}
            <FormControl fullWidth variant="outlined">
              <InputLabel shrink required>
                {translate("pages.profile.changeEmail.newEmail")}
              </InputLabel>
              <OutlinedInput
                type="email"
                value={newEmail}
                onChange={handleEmailChange}
                disabled={isLoading}
                inputProps={{ "data-testid": "new-email-input" }}
              />
            </FormControl>

            {/* Error message */}
            {error && (
              <Typography color="error.dark" variant="body2" data-testid="error-message">
                {error}
              </Typography>
            )}

            {/* Submit button */}
            <CardButton
              fullWidth
              height={52}
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid}
              data-testid="submit-button"
            >
              {translate("buttons.submit")}
            </CardButton>
          </Stack>
        </CardContent>
      </Card>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        open={showVerificationModal}
        maskedEmail={maskedEmail}
        onClose={handleVerificationClose}
        onSuccess={handleVerificationSuccess}
      />
    </Box>
  );
};
